import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMapGL, { MapRef, ViewState } from "react-map-gl";
import { useTranslation } from "react-i18next";
import styles from "../../main/ObjectDetails/ObjectDetails.module.scss";
import AnimatedDetailsContainer from "../../../../share/ResultDetails/AnimatedDetailsContainer/AnimatedDetailsContainer";
import DetailsHeader from "../../../../share/ResultDetails/DetailsHeader/DetailsHeader";
import Details from "./ActiveTabs/Details";
import CustomMarker from "../../_mapComponents/CustomMarker/CustomMarker";
import { ICurrentMarker } from "../../../../models/CurrentMarker";
import { CurrentUSAIDObject } from "../../../../models/CurrentUSAIDObject";
import {
  defaultViewport,
  mapboxAccessToken,
} from "../../../../constants/mapConstants";
import util from "../../../../util";
import useSelectedLanguage from "../../../../hooks/useSelectedLanguage";
import JpBlue from "../../../../assets/svg/JpBlue.svg";
import NpGreen from "../../../../assets/svg/NpGreen.svg";

interface ListViewUSAIDMapDetailsProps {
  company: CurrentUSAIDObject;
  isDetailsActive: boolean;
  onClose: () => void;
}

const USAID_MAP_STYLE = "mapbox://styles/aimap/clktdrslc00sq01qyhi0mgpc9";

const ListViewUSAIDMapDetails: React.FC<ListViewUSAIDMapDetailsProps> = ({
  company,
  isDetailsActive,
  onClose,
}) => {
  const selectedLanguage = useSelectedLanguage();
  const { t: tObjectDetails } = useTranslation("translation", {
    keyPrefix: "mainPage.mapFilters.objectDetails",
  });

  const mapRef = useRef<MapRef | null>(null);
  const [viewState, setViewState] = useState<ViewState>(() => {
    const initialLat = Number(company?.lat);
    const initialLon = Number(company?.lon);
    const hasInitialCoordinates =
      Number.isFinite(initialLat) && Number.isFinite(initialLon);

    return {
      latitude: hasInitialCoordinates ? initialLat : defaultViewport.latitude,
      longitude: hasInitialCoordinates ? initialLon : defaultViewport.longitude,
      zoom: hasInitialCoordinates ? 13 : defaultViewport.zoom,
      bearing: 0,
      pitch: 0,
    };
  });

  useEffect(() => {
    const rawLat = Number(company?.lat);
    const rawLon = Number(company?.lon);
    const hasCoordinates = Number.isFinite(rawLat) && Number.isFinite(rawLon);

    setViewState((prev) => ({
      ...prev,
      latitude: hasCoordinates ? rawLat : defaultViewport.latitude,
      longitude: hasCoordinates ? rawLon : defaultViewport.longitude,
      zoom: hasCoordinates ? 13 : defaultViewport.zoom,
    }));
  }, [company?.lat, company?.lon]);

  const marker = useMemo<ICurrentMarker>(() => {
    const latitude = Number(company?.lat);
    const longitude = Number(company?.lon);

    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return { lat: latitude, lon: longitude };
    }

    return null;
  }, [company?.lat, company?.lon]);

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (map) {
      util.mapInterfaceUtil.changeMapboxLanguage(map, selectedLanguage);
      util.mapInterfaceUtil.addPinIconToMapbox(map);
    }
  }, [selectedLanguage]);

  const description = company?.address || "";
  const title = company?.name || "";

  const logo =
    company?.entity_type === "Юридична особа" ? (
      <img src={JpBlue} alt="" />
    ) : (
      <img src={NpGreen} alt="" />
    );

  return (
    <AnimatedDetailsContainer isShown={isDetailsActive}>
      <DetailsHeader
        title={title}
        description={description}
        closeDetails={onClose}
        logo={logo}
      />
      <div className={styles.listViewDetailsLayout}>
        <div className={styles.listViewMapWrapper}>
          <ReactMapGL
            {...viewState}
            ref={(instance) => (mapRef.current = instance)}
            mapboxAccessToken={mapboxAccessToken}
            mapStyle={USAID_MAP_STYLE}
            onMove={(event) => setViewState(event.viewState)}
            dragRotate={false}
            doubleClickZoom={false}
            scrollZoom={false}
            attributionControl={false}
            style={{ width: "100%", height: "100%" }}
            onLoad={handleMapLoad}
          >
            {marker && <CustomMarker marker={marker} />}
          </ReactMapGL>
        </div>
        <div className={styles.listViewPanels}>
          <section className={styles.listViewPanel}>
            <h3 className={styles.listViewPanelTitle}>
              {tObjectDetails("details.title")}
            </h3>
            <Details
              key={`details-${company?.id ?? company?.name}`}
              contentClassName={styles.listViewPanelContent}
            />
          </section>
        </div>
      </div>
    </AnimatedDetailsContainer>
  );
};

export default ListViewUSAIDMapDetails;
